// My phone (docs/PBX.md, phase 6): the signed-in user's own extension. Do not disturb, forwarding, follow-me,
// voicemail (listen, delete, PIN, e-mail), the calls of the extension, and, for USER accounts, a browser phone.
// USER accounts see only this page (UserLayout); agents and admins with an extension open it from their menu.
import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut, Moon, Sun, Play, Trash2, Phone } from 'lucide-react';
import { api, errorText, blobUrl } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input, Checkbox } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge, StatusBadge } from '@/components/ui/badge';
import { Field, ErrorBox, DataTable, PageHeader, Loading } from '@/components/common';
import { Softphone } from '@/components/Softphone';
import { LanguageMenu, applyUserLanguage } from '@/components/LanguageMenu';
import { useTheme } from '@/components/layout/AdminLayout';
import { usePhone } from '@/store/phone';
import { useAuth, adminPortal } from '@/store/auth';
import { toast } from '@/store/toast';
import { fmtDuration, fmtTime, enumLabel } from '@/lib/utils';
import { useBranding, BrandLogo } from '@/lib/branding';
import { PbxTargetInput } from '@/components/PbxPickers';
import { t } from '@/i18n';

interface MyPhone {
  extension: Record<string, any> & { number: string; name: string };
  voicemail: { box: string; email: string | null; email_attach: number; new: number; saved: number } | null;
  devices: { id: number; label: string; sip_username: string; sip_registered: number; sip_user_agent: string | null }[];
  followme: { seq: number; number: string; ring_sec: number }[];
}

const FWD = [['fwd_always', 'Always'], ['fwd_busy', 'When busy'], ['fwd_noanswer', 'When not answered'], ['fwd_unavailable', 'When no phone is on']] as const;

export function MyPhonePage({ browserPhone = false }: { browserPhone?: boolean }) {
  const q = useQuery({ queryKey: ['me-phone'], queryFn: () => api<MyPhone>('/me/phone') });
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={q.error} />;
  const d = q.data;
  return (
    <div className="grid gap-6">
      <PageHeader title={t('My phone')} description={t('Extension {{number}} · {{name}}', { number: d.extension.number, name: d.extension.name })} />
      <div className="grid gap-6 lg:grid-cols-2">
        <Settings data={d} onSaved={() => q.refetch()} />
        <div className="grid content-start gap-6">
          {browserPhone && <BrowserPhone />}
          <SpeedDials />
          <Card>
            <CardHeader><CardTitle>{t('My phones')}</CardTitle></CardHeader>
            <CardContent>
              <DataTable rowKey={(r) => r.id} rows={d.devices} columns={[
                { key: 'label', label: t('Label') }, { key: 'sip_username', label: t('SIP user') },
                { key: 'sip_registered', label: t('SIP'), render: (r) => Number(r.sip_registered) ? <Badge variant="success">{t('registered')}</Badge> : <Badge variant="secondary">{t('offline')}</Badge> },
                { key: 'sip_user_agent', label: t('Phone') }]} />
            </CardContent>
          </Card>
        </div>
      </div>
      <Voicemails onChange={() => q.refetch()} />
      <Calls />
    </div>
  );
}

function Settings({ data, onSaved }: { data: MyPhone; onSaved: () => void }) {
  const e = data.extension;
  const [v, setV] = React.useState(() => ({
    dnd: !!Number(e.dnd), followme_enabled: !!Number(e.followme_enabled), ring_sec: String(e.ring_sec ?? 25),
    fwd_always: e.fwd_always ?? '', fwd_busy: e.fwd_busy ?? '', fwd_noanswer: e.fwd_noanswer ?? '', fwd_unavailable: e.fwd_unavailable ?? '',
    followme: data.followme.map((f) => f.number) as (string | null)[], pin: '', email: data.voicemail?.email ?? '', email_attach: !!Number(data.voicemail?.email_attach ?? 1),
  }));
  const [error, setError] = React.useState<unknown>(null);
  const set = (k: string, val: unknown) => setV((s) => ({ ...s, [k]: val }));
  async function save(ev: React.FormEvent) {
    ev.preventDefault(); setError(null);
    try {
      await api('/me/phone', { method: 'PATCH', body: {
        dnd: v.dnd, followme_enabled: v.followme_enabled, ring_sec: Number(v.ring_sec) || 25,
        fwd_always: v.fwd_always || null, fwd_busy: v.fwd_busy || null, fwd_noanswer: v.fwd_noanswer || null, fwd_unavailable: v.fwd_unavailable || null,
        ...(data.voicemail ? { voicemail: { email: v.email || null, email_attach: v.email_attach, ...(v.pin ? { pin: v.pin } : {}) } } : {}),
      } });
      const numbers = v.followme.filter((n): n is string => !!n);
      if (numbers.join(',') !== data.followme.map((f) => f.number).join(',')) await api('/me/followme', { method: 'PUT', body: { numbers } });
      toast.ok(t('Saved')); set('pin', ''); onSaved();
    } catch (err) { setError(err); }
  }
  return (
    <Card>
      <CardHeader><CardTitle>{t('Settings')}</CardTitle></CardHeader>
      <CardContent>
        <form onSubmit={save} className="grid gap-3" data-testid="my-phone-settings">
          <label className="flex items-center gap-2 text-sm"><Checkbox name="dnd" checked={v.dnd} onChange={(x) => set('dnd', x.target.checked)} /> {t('Do not disturb (calls go to voicemail or the busy forwarding)')}</label>
          <p className="text-xs text-muted-foreground">{t('Forward to an extension, a group, your voicemail or an outside number.')}</p>
          <div className="grid gap-3">
            {FWD.map(([k, label]) => (
              <Field help={`myphone.${k}`} key={k} label={t(label)}><PbxTargetInput name={k} source="me" value={(v as any)[k]} onChange={(x) => set(k, x ?? '')} /></Field>
            ))}
          </div>
          <Field help="myphone.ring_sec" label={t('Ring my phones for (seconds)')}><Input name="ring_sec" type="number" min={5} max={120} value={v.ring_sec} onChange={(x) => set('ring_sec', x.target.value)} /></Field>
          <label className="flex items-center gap-2 text-sm"><Checkbox name="followme_enabled" checked={v.followme_enabled} onChange={(x) => set('followme_enabled', x.target.checked)} /> {t('Follow-me: when I do not answer, ring these numbers in turn')}</label>
          <div className="grid gap-1.5 text-sm">
            <span className="font-medium">{t('Follow-me numbers')} <span className="text-xs font-normal text-muted-foreground">({t('rung in this order')})</span></span>
            <div className="grid gap-1" data-testid="followme-list">
              {v.followme.map((n, i) => (
                <div key={i} className="grid grid-cols-[1fr_auto] gap-1">
                  <PbxTargetInput name={`followme_${i}`} source="me" targets={['EXTENSION', 'OUTSIDE']} value={n}
                    onChange={(x) => set('followme', v.followme.map((m, j) => (j === i ? x : m)))} />
                  <Button type="button" size="icon" variant="ghost" aria-label={t('Remove')} onClick={() => set('followme', v.followme.filter((_, j) => j !== i))}><Trash2 /></Button>
                </div>
              ))}
              {v.followme.length < 10 && <div><Button type="button" size="sm" variant="outline" onClick={() => set('followme', [...v.followme, null])}>{t('Add a number')}</Button></div>}
            </div>
          </div>
          {data.voicemail && (<>
            <h3 className="mt-2 text-sm font-semibold">{t('Voicemail box {{box}}', { box: data.voicemail.box })}</h3>
            <Field help="myphone.vm_pin" label={t('New PIN')} hint={t('4 or more digits; empty keeps the PIN')}><Input name="pin" type="password" inputMode="numeric" autoComplete="new-password" value={v.pin} onChange={(x) => set('pin', x.target.value)} /></Field>
            <Field help="myphone.vm_email" label={t('Send new messages to this e-mail')}><Input name="email" type="email" value={v.email} onChange={(x) => set('email', x.target.value)} /></Field>
            <label className="flex items-center gap-2 text-sm"><Checkbox name="email_attach" checked={v.email_attach} onChange={(x) => set('email_attach', x.target.checked)} /> {t('Attach the recording to the e-mail')}</label>
          </>)}
          <ErrorBox error={error} />
          <div className="flex justify-end"><Button type="submit">{t('Save')}</Button></div>
        </form>
      </CardContent>
    </Card>
  );
}

function Voicemails({ onChange }: { onChange: () => void }) {
  const q = useQuery({ queryKey: ['me-voicemails'], queryFn: () => api<{ rows: any[] }>('/me/voicemails') });
  const [playing, setPlaying] = React.useState<{ id: number; url: string } | null>(null);
  React.useEffect(() => () => { if (playing) URL.revokeObjectURL(playing.url); }, [playing]);
  async function play(id: number) {
    try {
      setPlaying({ id, url: await blobUrl(`/me/voicemails/${id}/file`) });
      q.refetch(); onChange();
    } catch (e) { toast.error(errorText(e)); }
  }
  async function remove(id: number) {
    if (!confirm(t('Delete this message?'))) return;
    try { await api(`/me/voicemails/${id}`, { method: 'DELETE' }); q.refetch(); onChange(); } catch (e) { toast.error(errorText(e)); }
  }
  return (
    <Card>
      <CardHeader><CardTitle>{t('Voicemail')}</CardTitle></CardHeader>
      <CardContent>
        <ErrorBox error={q.error} />
        {playing && <audio controls autoPlay src={playing.url} className="mb-3 w-full" data-testid="vm-player" />}
        <DataTable rowKey={(r) => r.id} rows={q.data?.rows} empty={t('No messages')} columns={[
          { key: 'created_at', label: t('When'), render: (r) => fmtTime(r.created_at) }, { key: 'caller_number', label: t('From') },
          { key: 'duration_sec', label: t('Length'), render: (r) => fmtDuration(r.duration_sec) }, { key: 'status', label: t('Status'), render: (r) => <StatusBadge value={r.status} /> }]}
          actions={(r) => (<>
            <Button size="icon" variant="ghost" aria-label={t('Play')} onClick={() => play(r.id)}><Play /></Button>
            <Button size="icon" variant="ghost" aria-label={t('Delete')} onClick={() => remove(r.id)}><Trash2 className="text-destructive" /></Button>
          </>)} />
      </CardContent>
    </Card>
  );
}

// speed dial (2026-10-15): the extension's own entries (slot, name, number), saved as a whole; the company's are listed
type Speed = { slot: string; name: string; number: string | null };
function SpeedDials() {
  const q = useQuery({ queryKey: ['me-speed-dials'], queryFn: () => api<{ company: any[]; mine: any[] }>('/me/speed-dials') });
  const [rows, setRows] = React.useState<Speed[] | null>(null);
  const [error, setError] = React.useState<unknown>(null);
  const list = rows ?? (q.data?.mine ?? []).map((r) => ({ slot: String(r.slot), name: r.name, number: r.number }));
  const edit = (i: number, k: keyof Speed, val: string | null) => setRows(list.map((r, j) => (j === i ? { ...r, [k]: val } : r)));
  async function save() {
    setError(null);
    try {
      await api('/me/speed-dials', { method: 'PUT', body: { entries: list.filter((r) => r.number).map((r) => ({ slot: Number(r.slot), name: r.name, number: r.number })) } });
      toast.ok(t('Saved')); setRows(null); q.refetch();
    } catch (e) { setError(e); }
  }
  const nextSlot = () => { for (let n = 1; n < 1000; n++) if (!list.some((r) => Number(r.slot) === n)) return String(n); return '0'; };
  return (
    <Card>
      <CardHeader><CardTitle>{t('Speed dial')}</CardTitle></CardHeader>
      <CardContent className="grid gap-3 text-sm">
        <p className="text-xs text-muted-foreground">{t('Dial the speed dial code and the slot (*05 calls slot 5). Your entries win over the company\'s.')}</p>
        <div className="grid gap-1" data-testid="speed-list">
          {list.map((r, i) => (
            <div key={i} className="grid grid-cols-[4rem_8rem_1fr_auto] items-center gap-1">
              <Input name={`speed_slot_${i}`} aria-label={t('Slot')} inputMode="numeric" value={r.slot} onChange={(e) => edit(i, 'slot', e.target.value.replace(/\D/g, '').slice(0, 3))} />
              <Input name={`speed_name_${i}`} aria-label={t('Name')} value={r.name} onChange={(e) => edit(i, 'name', e.target.value)} />
              <PbxTargetInput name={`speed_${i}`} source="me" targets={['EXTENSION', 'RING_GROUP', 'CONFERENCE', 'PAGING', 'OUTSIDE']} value={r.number} onChange={(x) => edit(i, 'number', x)} />
              <Button type="button" size="icon" variant="ghost" aria-label={t('Remove')} onClick={() => setRows(list.filter((_, j) => j !== i))}><Trash2 /></Button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Button type="button" size="sm" variant="outline" onClick={() => setRows([...list, { slot: nextSlot(), name: '', number: null }])}>{t('Add a speed dial')}</Button>
          {rows && <Button type="button" size="sm" onClick={save}>{t('Save')}</Button>}
        </div>
        <ErrorBox error={error} />
        {!!q.data?.company.length && (
          <div data-testid="speed-company">
            <span className="text-xs font-medium text-muted-foreground">{t('Company speed dials')}</span>
            <ul className="mt-1 grid gap-0.5">{q.data.company.map((r) => <li key={r.id}><code>{r.slot}</code> {r.name} · {r.number}</li>)}</ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Calls() {
  const q = useQuery({ queryKey: ['me-calls'], queryFn: () => api<{ rows: any[] }>('/me/calls') });
  return (
    <Card>
      <CardHeader><CardTitle>{t('My calls')}</CardTitle></CardHeader>
      <CardContent>
        <ErrorBox error={q.error} />
        <DataTable rowKey={(r) => r.call_uuid} rows={q.data?.rows} empty={t('No calls yet')} columns={[
          { key: 'start_time', label: t('When'), render: (r) => fmtTime(r.start_time) },
          { key: 'way', label: t('Direction'), render: (r) => (r.way === 'OUT' ? t('made') : t('received')) },
          { key: 'source', label: t('From') }, { key: 'destination', label: t('To') },
          { key: 'talk_sec', label: t('Talk'), render: (r) => fmtDuration(r.talk_sec ?? r.billsec) },
          { key: 'hangup_cause', label: t('Result'), render: (r) => enumLabel(r.hangup_cause) }]} />
      </CardContent>
    </Card>
  );
}

// the browser phone of a USER account: a SIP account of the extension made for this browser
function BrowserPhone() {
  const phone = usePhone();
  const [busy, setBusy] = React.useState(false);
  async function connect() {
    setBusy(true);
    try {
      const r = await api<{ sip: Parameters<typeof phone.connect>[0] }>('/me/phone/browser', { body: {} });
      await phone.connect(r.sip);
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  }
  return phone.reg === 'disconnected' || phone.reg === 'failed' ? (
    <Card>
      <CardHeader><CardTitle>{t('Browser phone')}</CardTitle></CardHeader>
      <CardContent className="grid gap-2 text-sm">
        <p>{t('Make and take calls of your extension in this browser (microphone needed).')}</p>
        {phone.regError && <p className="text-destructive">{phone.regError}</p>}
        <div><Button disabled={busy} onClick={connect} data-testid="browser-phone-on"><Phone /> {t('Turn on the browser phone')}</Button></div>
      </CardContent>
    </Card>
  ) : <Softphone />;
}

// the page frame of a USER account: no menu, only their phone
export function UserLayout() {
  const { user, logout, setTenant, setPermissions, setExtension } = useAuth();
  const brand = useBranding();
  const theme = useTheme();
  const nav = useNavigate();
  const qc = useQueryClient();
  const phone = usePhone();
  const me = useQuery({ queryKey: ['me', user?.id], queryFn: () => api<any>('/auth/me') });
  React.useEffect(() => {
    if (me.data) { setTenant(me.data.tenant, me.data.home_tenant_id); setPermissions(me.data.permissions ?? null); setExtension(me.data.extension ?? null); applyUserLanguage(me.data); }
  }, [me.data, setTenant, setPermissions, setExtension]);
  async function signOut() {
    await phone.disconnect().catch(() => {});
    logout(); qc.clear(); nav('/login');
  }
  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b bg-background/95 px-4 backdrop-blur">
        <BrandLogo className="h-5 w-5" /> <span className="font-semibold">{brand.product_name}</span>
        <div className="flex-1" />
        {adminPortal(user) && <Link to="/admin" className="rounded-md px-3 py-1.5 text-sm hover:bg-accent" data-testid="to-admin">{t('Admin portal')}</Link>}
        <LanguageMenu />
        <Button variant="ghost" size="icon" aria-label={t('Toggle theme')} onClick={theme.toggle}>{theme.dark ? <Sun /> : <Moon />}</Button>
        <span className="text-sm text-muted-foreground" data-testid="whoami">{user?.name}</span>
        <Button variant="outline" size="sm" onClick={signOut}><LogOut /> {t('Sign out')}</Button>
      </header>
      <main className="mx-auto max-w-6xl p-4 md:p-6">
        {me.data && !me.data.extension ? <p className="text-sm text-muted-foreground" data-testid="no-extension">{t('Your account has no extension yet. Ask an administrator.')}</p> : <MyPhonePage browserPhone />}
      </main>
    </div>
  );
}
